import { format } from 'date-fns'
import {
  convertPlatesToPolygons,
  convertPolygonsToWeldMap,
  ExternalUserFieldAccess,
  FormField,
  GeckoAssetModelType,
  GeckoComponentRect,
  GeckoHorizontalWeldJSON,
  GeckoMeasurement,
  GeckoMeasurementJson,
  GeckoMeasurementUnit,
  GeckoPlateJSON,
  GeckoPolygonalPlateJSON,
  GeckoShipDeckBittJSON,
  GeckoShipDeckChockJSON,
  GeckoShipDeckHatchJSON,
  GeckoVerticalWeldJSON,
  getFormFieldByDataKey,
  InputType,
  RolodexEntryJson,
  ShipDeckAssetModelJSON,
  ShipHullAssetModelJSON,
  WeldedPolygon,
  WrappingDirection,
} from 'types'
import { GeckoAssetModel } from 'types/entries/geckoAssetModel'
import { EntryPutJson } from 'types/helpers'
import { parseDate } from 'utils/parsingHelpers'
import {
  optionalDateStringWithRegex,
  optionalNumberSchema,
  optionalStringSchema,
} from 'utils/zodHelpers'

import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from './assetModelEnums'
import { regenerateBasicShipHullComponent } from './assetModelHelpers'
import { BaseAssetModelData } from './baseAssetModelData'

export type GeckoShipAssetModelJSON = {
  name: string
  type: GeckoAssetModelType.Ship
  installation_date: string | undefined
  components: Record<string, ShipDeckAssetModelJSON | ShipHullAssetModelJSON>
  effective_date: string
  compass_orientation: number | undefined
  plant_north_compass_orientation: number | undefined
  ship_class: string | undefined
  vessel_name: string | undefined
  ship_class_number: number | undefined
  ship_status: string | undefined
  fleet: string | undefined
  date_status_changed: string | undefined
  homeport: string | undefined
  delivery_date: string | undefined
  commission_date: string | undefined
  uic: number | undefined
  frame_size: GeckoMeasurementJson | undefined
}

export const ShipAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.ShipDeck,
  GeckoComponentModelType.BasicShipHull,
]

export const NewShipAssetModelJSON = {
  name: 'New Asset Model Ship',
  type: GeckoAssetModelType.Ship,
  components: {},
  effective_date: format(Date.now(), 'yyyy-MM-dd'),
  installation_date: undefined,
  compass_orientation: undefined,
  plant_north_compass_orientation: undefined,
  ship_class: undefined,
  vessel_name: undefined,
  ship_class_number: undefined,
  ship_status: undefined,
  fleet: undefined,
  date_status_changed: undefined,
  homeport: undefined,
  delivery_date: undefined,
  commission_date: undefined,
  uic: undefined,
  frame_size: undefined,
} as GeckoShipAssetModelJSON

export class GeckoShipAssetModel extends GeckoAssetModel<
  BaseAssetModelData & {
    ship_class: string | undefined
    vessel_name: string | undefined
    ship_class_number: number | undefined
    ship_status: string | undefined
    fleet: string | undefined
    date_status_changed: string | undefined
    homeport: string | undefined
    delivery_date: string | undefined
    commission_date: string | undefined
    uic: number | undefined
    frame_size: GeckoMeasurement | undefined
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(json)
    const recordData = json.data as Record<string, unknown>
    this.data.ship_class = recordData.ship_class as string | undefined
    this.data.vessel_name = recordData.vessel_name as string | undefined
    this.data.ship_class_number = recordData.ship_class_number as
      | number
      | undefined
    this.data.ship_status = recordData.ship_status as string | undefined
    this.data.fleet = recordData.fleet as string | undefined
    this.data.date_status_changed = recordData.date_status_changed as
      | string
      | undefined
    this.data.homeport = recordData.homeport as string | undefined
    this.data.delivery_date = recordData.delivery_date as string | undefined
    this.data.commission_date = recordData.commission_date as string | undefined
    this.data.uic = recordData.uic as number | undefined
    this.data.frame_size = recordData.frame_size
      ? GeckoMeasurement.CreateFromJSON(
          recordData.frame_size as GeckoMeasurementJson
        )
      : undefined
  }

  public static CreateFromJSON(json: RolodexEntryJson): GeckoShipAssetModel {
    return new GeckoShipAssetModel(json)
  }

  public asJSON(): GeckoShipAssetModelJSON {
    return generateShipAssetModel(
      this.data.name,
      this.raw.data.components as Record<
        string,
        ShipDeckAssetModelJSON | ShipHullAssetModelJSON
      >,
      this.data.effective_date,
      this.data.ship_class as string | undefined,
      this.data.vessel_name as string | undefined,
      this.data.ship_class_number as number | undefined,
      this.data.ship_status as string | undefined,
      this.data.fleet as string | undefined,
      this.data.date_status_changed as string | undefined,
      this.data.homeport as string | undefined,
      this.data.delivery_date as string | undefined,
      this.data.commission_date as string | undefined,
      this.data.uic as number | undefined,
      this.data.installation_date,
      this.data.compass_orientation,
      this.data.plant_north_compass_orientation,
      this.data.frame_size?.toJSON()
    )
  }
}

export function generateShipAssetModel(
  name: string,
  components: Record<string, ShipDeckAssetModelJSON | ShipHullAssetModelJSON>,
  effective_date: Date,
  ship_class: string | undefined,
  vessel_name: string | undefined,
  ship_class_number: number | undefined,
  ship_status: string | undefined,
  fleet: string | undefined,
  date_status_changed: string | undefined,
  homeport: string | undefined,
  delivery_date: string | undefined,
  commission_date: string | undefined,
  uic: number | undefined,
  installation_date: Date | undefined,
  compass_orientation: number | undefined,
  plant_north_compass_orientation: number | undefined,
  frame_size: GeckoMeasurementJson | undefined
): GeckoShipAssetModelJSON {
  const shipEffectiveDate = effective_date.toISOString().split('T')[0]
  const shipInstallationDate = installation_date
    ? installation_date.toISOString().split('T')[0]
    : undefined

  return {
    name: name,
    type: GeckoAssetModelType.Ship,
    components: components,
    effective_date: shipEffectiveDate,
    installation_date: shipInstallationDate,
    compass_orientation: compass_orientation,
    plant_north_compass_orientation: plant_north_compass_orientation,
    ship_class: ship_class,
    vessel_name: vessel_name,
    ship_class_number: ship_class_number,
    ship_status: ship_status,
    fleet: fleet,
    date_status_changed: date_status_changed,
    homeport: homeport,
    delivery_date: delivery_date,
    commission_date: commission_date,
    uic: uic,
    frame_size: frame_size,
  }
}

export const shipModelFormFields = (
  assetModelJSON: GeckoShipAssetModelJSON
): FormField[] => {
  const shipModelFormFields: FormField[] = [
    {
      dataKey: 'ship_class',
      label: 'Ship Class',
      value: assetModelJSON.ship_class,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'vessel_name',
      label: 'Vessel Name',
      value: assetModelJSON.vessel_name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'ship_class_number',
      label: 'Ship Class Number',
      value: assetModelJSON.ship_class_number,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'ship_status',
      label: 'Ship Status',
      value: assetModelJSON.ship_status,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'fleet',
      label: 'Fleet',
      value: assetModelJSON.fleet,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'date_status_changed',
      label: 'Date Status Changed',
      value: assetModelJSON.date_status_changed,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Date,
    },
    {
      dataKey: 'homeport',
      label: 'Homeport',
      value: assetModelJSON.homeport,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'delivery_date',
      label: 'Delivery Date',
      value: assetModelJSON.delivery_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Date,
    },
    {
      dataKey: 'commission_date',
      label: 'Commission Date',
      value: assetModelJSON.commission_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Date,
    },
    {
      dataKey: 'uic',
      label: 'UIC',
      value: assetModelJSON.uic,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'frame_size',
      label: 'Frame Size',
      value: assetModelJSON.frame_size?.num,
      unit_magnitude: assetModelJSON.frame_size?.unit
        ? (GeckoMeasurementUnit.GetByID(assetModelJSON.frame_size?.unit) ??
          GeckoMeasurementUnit.None)
        : GeckoMeasurementUnit.None,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
  return shipModelFormFields
}

export const ShipJSONFromFormFields = (
  assetModelJSON: GeckoShipAssetModelJSON,
  formFields: FormField[]
): GeckoShipAssetModelJSON => {
  const shipClass = getFormFieldByDataKey(formFields, 'ship_class')?.value as
    | string
    | undefined
  const vesselName = getFormFieldByDataKey(formFields, 'vessel_name')?.value as
    | string
    | undefined
  const shipClassNumber = getFormFieldByDataKey(formFields, 'ship_class_number')
    ?.value as number | undefined
  const shipStatus = getFormFieldByDataKey(formFields, 'ship_status')?.value as
    | string
    | undefined
  const fleet = getFormFieldByDataKey(formFields, 'fleet')?.value as
    | string
    | undefined
  const dateStatusChanged = getFormFieldByDataKey(
    formFields,
    'date_status_changed'
  )?.value as string | undefined
  const homeport = getFormFieldByDataKey(formFields, 'homeport')?.value as
    | string
    | undefined
  const deliveryDate = getFormFieldByDataKey(formFields, 'delivery_date')
    ?.value as string | undefined
  const commissionDate = getFormFieldByDataKey(formFields, 'commission_date')
    ?.value as string | undefined
  const uic = getFormFieldByDataKey(formFields, 'uic')?.value as
    | number
    | undefined
  let frameSize = undefined
  const frameSizeFormField = getFormFieldByDataKey(formFields, 'frame_size')
  if (
    frameSizeFormField &&
    frameSizeFormField.unit_magnitude instanceof GeckoMeasurementUnit
  ) {
    frameSize = {
      num: frameSizeFormField.value as number,
      unit: frameSizeFormField.unit_magnitude?.id ?? '',
    }
  }

  const installationDate = undefined
  const compassOrientation = undefined
  const plantNorthCompassOrientation = undefined

  return generateShipAssetModel(
    assetModelJSON.name,
    assetModelJSON.components,
    parseDate(assetModelJSON.effective_date) as Date,
    shipClass,
    vesselName,
    shipClassNumber,
    shipStatus,
    fleet,
    dateStatusChanged,
    homeport,
    deliveryDate,
    commissionDate,
    uic,
    installationDate,
    compassOrientation,
    plantNorthCompassOrientation,
    frameSize
  )
}

export function regenerateShipAssetModel(
  assetModel: GeckoAssetModel
): EntryPutJson {
  const rawAssetModel = assetModel.raw
  if (!rawAssetModel.data.components) {
    throw new Error(
      'Unable to regenerate this Asset Model! Components seem to be undefined.'
    )
  }
  const regeneratedComponents = {} as Record<string, unknown>
  for (const [componentID, component] of Object.entries(
    rawAssetModel.data.components
  )) {
    switch (component.type) {
      case GeckoComponentModelType.ShipDeck:
        regeneratedComponents[componentID] =
          regenerateShipDeckComponent(component)
        break
      case GeckoComponentModelType.BasicShipHull:
        regeneratedComponents[componentID] =
          regenerateBasicShipHullComponent(component)
        break
      default:
        throw new Error('Unable to determine this type of Ship Component!')
    }
  }
  assetModel.raw.data.components = regeneratedComponents
  return assetModel.raw
}

export function regenerateShipDeckComponent(
  shipDeck: Record<string, unknown>
): Record<string, unknown> {
  const shipFeatures = shipDeck.features as Record<
    string,
    | GeckoShipDeckBittJSON
    | GeckoShipDeckChockJSON
    | GeckoShipDeckHatchJSON
    | GeckoPlateJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoPolygonalPlateJSON
  >
  // If there's no features on the asset model, there's nothing to regenerate
  if (Object.keys(shipFeatures).length === 0) {
    return shipDeck
  }

  const shipDeckBitts = new Map<string, GeckoShipDeckBittJSON>()
  const shipDeckChocks = new Map<string, GeckoShipDeckChockJSON>()
  const shipDeckHatches = new Map<string, GeckoShipDeckHatchJSON>()
  const shipPlates = new Map<string, GeckoPlateJSON>()
  const shipPolygonalPlates = new Map<string, GeckoPolygonalPlateJSON>()

  for (const [featureID, feature] of Object.entries(shipFeatures)) {
    if (!feature.type) {
      console.error(
        `Unable to regenerate some features, as feature.type is missing!`
      )
    }
    switch (feature.type) {
      case GeckoFeatureModelType.ShipDeckBitt:
        shipDeckBitts.set(featureID, feature as GeckoShipDeckBittJSON)
        break
      case GeckoFeatureModelType.ShipDeckChock:
        shipDeckChocks.set(featureID, feature as GeckoShipDeckChockJSON)
        break
      case GeckoFeatureModelType.ShipDeckHatch:
        shipDeckHatches.set(featureID, feature as GeckoShipDeckHatchJSON)
        break
      case GeckoFeatureModelType.Plate:
        shipPlates.set(featureID, feature as GeckoPlateJSON)
        break
      case GeckoFeatureModelType.PolygonalPlate:
        shipPolygonalPlates.set(featureID, feature as GeckoPolygonalPlateJSON)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue

      default:
        throw new Error('Unable to determine this type of Ship Feature!')
    }
  }

  const componentBoundsJSON = shipDeck.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    shipDeck.id as string,
    componentBoundsJSON.x_start as number,
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end as number,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.None
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...shipPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoShipDeckBittJSON
    | GeckoShipDeckChockJSON
    | GeckoShipDeckHatchJSON
    | GeckoPlateJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoPolygonalPlateJSON
  >([
    ...shipPlates.entries(),
    ...welds.entries(),
    ...shipDeckBitts.entries(),
    ...shipDeckChocks.entries(),
    ...shipDeckHatches.entries(),
    ...shipPolygonalPlates.entries(),
  ])

  shipDeck.features = Object.fromEntries(features) as Record<string, unknown>

  return shipDeck
}
